<template>
  <div class="task-info-requests-section" data-testid="task-info-requests-section">
    <div class="task-info-requests-section__header">
      <span class="task-info-requests-section__title">Information Requests</span>
      <button
        type="button"
        class="task-info-requests-section__new-btn"
        data-testid="task-info-requests-section__new-btn"
        @click="openRequestModal"
      >
        <v-icon x-small>mdi-plus</v-icon>
        <span>New Request</span>
      </button>
    </div>

    <InfoRequest
      :task="task"
      :metadata="metadata"
      :inforeqstage="inforeqstage"
      data-testid="task-info-requests-section__list"
      @approve="$emit('approve', $event)"
      @reject="$emit('reject', $event)"
    />

    <AddEditRequestModal
      v-model="requestModalOpen"
      :loading="requestModalLoading"
      :request="editingRequest"
      :errors="requestFormErrors"
      :info-owner-list="infoOwnerList"
      :priority-list="priorityList"
      :category-list="categoryList"
      :location-list="locationList"
      :location-header="$store.state.LocationHeader || 'Location'"
      :min-date="$nova.formatDateISO(new Date())"
      :task-options="taskOptions"
      :project-id="cycleId"
      data-testid="task-info-requests-section__form-modal"
      @save="onSaveRequest"
      @clear-error="clearRequestFormError"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import InfoRequest from './InfoRequest.vue';
import { AddEditRequestModal } from '@/modules/financialclose/views/Project/InformationRequests/modals';
import { INFORMATION_MANAGER_PRIORITY_LIST } from '@/modules/financialclose/utils/constants';
import { validateInformationRequest } from '@/modules/financialclose/utils/informationManager';
import { prepareRequestsForUpdate } from '@/modules/financialclose/utils/informationManagerComponentUtils';

export default {
  name: 'TaskInfoRequestsSection',

  components: {
    InfoRequest,
    AddEditRequestModal,
  },

  props: {
    task: { type: Object, required: true },
    metadata: { type: Object, default: () => ({}) },
    inforeqstage: { type: Number, default: 0 },
  },

  data() {
    return {
      requestModalOpen: false,
      requestModalLoading: false,
      editingRequest: null,
      requestFormErrors: {},
      priorityList: [...INFORMATION_MANAGER_PRIORITY_LIST],
    };
  },

  computed: {
    ...mapGetters('informationManager', {
      infoOwnerList: 'infoOwnerList',
      storeLocationList: 'locationList',
      storeFilteredLocationList: 'filteredLocationList',
      requestList: 'requestList',
    }),

    informationManagerState() {
      return this.$store.state.m_financialclose?.informationManager
        || this.$store.state.informationManager
        || {};
    },

    pbcProjectId() {
      return this.metadata?.pbcproject
        || this.informationManagerState.pbcprojectId
        || '';
    },

    cycleId() {
      return this.metadata?._id
        || this.$store.getters['tasks/getTaskListData']?.metadata?._id
        || this.task.project
        || this.$route.params.projectId
        || '';
    },

    locationList() {
      return this.storeFilteredLocationList?.length
        ? this.storeFilteredLocationList
        : (this.storeLocationList || []);
    },

    categoryList() {
      const categories = ['Others'];
      (this.requestList || []).forEach((request) => {
        if (request.category && request.category !== '' && !categories.includes(request.category)) {
          categories.push(request.category);
        }
      });
      return categories;
    },

    taskOptions() {
      return [{
        value: this.task._id,
        name: this.task.description,
        ref: this.task.ref,
        location: this.task.location,
      }];
    },
  },

  methods: {
    async ensureInformationManagerContext() {
      const cycleId = this.cycleId;
      if (!cycleId) return;

      const imState = this.informationManagerState;
      if (!imState.projectId || imState.projectId !== cycleId || !imState.metadata?._id) {
        await this.$store.dispatch('informationManager/fetchProjectContext', {
          projectId: cycleId,
        });
      }
    },

    async openRequestModal() {
      this.editingRequest = {
        reference: this.task._id ? [this.task._id] : [],
        ...(this.task.location ? { location: this.task.location } : {}),
      };
      this.requestFormErrors = {};

      await this.ensureInformationManagerContext();
      await this.$store.dispatch('informationManager/fetchInfoOwners');

      this.requestModalOpen = true;
    },

    clearRequestFormError(field) {
      if (this.requestFormErrors[field]) {
        this.$set(this.requestFormErrors, field, '');
      }
    },

    async onSaveRequest({ mode, data }) {
      if (mode !== 'add') return;

      const requestData = { ...data };
      const { errors, hasError } = validateInformationRequest(requestData);
      this.requestFormErrors = errors;
      if (hasError) {
        this.$store.commit('sbError', 'Please fill all required fields correctly.');
        return;
      }

      this.requestModalLoading = true;
      try {
        const pbcProjectId = this.pbcProjectId || this.informationManagerState.pbcprojectId || '';
        const [normalizedRequest] = prepareRequestsForUpdate(
          [requestData],
          { projectId: pbcProjectId, cycleId: this.cycleId },
        );
        const requestBatch = normalizedRequest ? [normalizedRequest] : [];
        const response = await this.$store.dispatch('informationManager/addRequest', {
          data: requestBatch,
        });

        if (response?.status === 'success') {
          this.requestModalOpen = false;
          this.editingRequest = null;
          this.$emit('refresh');
        } else if (response?.data?.[0]?.index0) {
          this.requestFormErrors = response.data[0].index0;
        }
      } catch (err) {
        if (err.data?.length > 0 && err.data[0].index0) {
          this.requestFormErrors = err.data[0].index0;
        }
      } finally {
        this.requestModalLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/summary-cards';
@import '../../styles/files-requests';

.task-info-requests-section {
  @include files-requests-panel;
}

.task-info-requests-section__header {
  @include files-requests-panel-header;
}

.task-info-requests-section__title {
  @include files-requests-panel-title;
}

.task-info-requests-section__new-btn {
  @include files-requests-action-btn;
}
</style>
